/**
 * ChatNotificationBubble — Floating draggable notification bubble
 *
 * Shows when a chat message arrives while the Crew modal is closed.
 * - 48x48 circle with sender's profile pic, magenta border + glow
 * - Unread count badge (red, top-right)
 * - Tap: emits 'bubble:tap' with peerId
 * - Hold + drag: repositionable, position saved to localStorage
 * - Drag to bottom dismiss zone: dismisses bubble
 * - z-index: 700 (above toast at 600)
 */

import { EventEmitter } from '../../engine/core/EventEmitter';

// ─── Constants ────────────────────────────────────────────────────────

const BUBBLE_SIZE = 48;
const BADGE_SIZE = 20;
const HOLD_DELAY = 200; // ms before drag starts
const DISMISS_ZONE_HEIGHT = 80;
const STORAGE_KEY = 'sdl_chat_bubble_pos';
const STYLE_ID = 'chat-notification-bubble-styles';

// ─── Types ────────────────────────────────────────────────────────────

interface BubbleEvents {
  'bubble:tap': { peerId: string };
  'bubble:dismiss': void;
}

export interface ChatNotificationBubbleOptions {
  container: HTMLElement;
}

export interface BubbleShowData {
  peerId: string;
  peerUsername?: string;
  picture?: string;
  unreadCount: number;
}

// ─── Component ────────────────────────────────────────────────────────

export class ChatNotificationBubble extends EventEmitter<BubbleEvents> {
  public element: HTMLElement;
  private container: HTMLElement;
  private imgEl: HTMLElement;
  private badgeEl: HTMLElement;
  private dismissZone: HTMLElement;

  // State
  private peerId: string = '';
  private visible = false;
  private dragging = false;
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  // Bound handlers
  private onPointerDown = this.handlePointerDown.bind(this);
  private onPointerMove = this.handlePointerMove.bind(this);
  private onPointerUp = this.handlePointerUp.bind(this);

  constructor(options: ChatNotificationBubbleOptions) {
    super();
    this.container = options.container;
    this.injectStyles();

    // Create bubble element
    this.element = document.createElement('div');
    this.element.className = 'chat-notif-bubble';

    this.imgEl = document.createElement('div');
    this.imgEl.className = 'chat-notif-bubble-img';
    this.element.appendChild(this.imgEl);

    this.badgeEl = document.createElement('div');
    this.badgeEl.className = 'chat-notif-bubble-badge';
    this.element.appendChild(this.badgeEl);

    // Dismiss zone (hidden until dragging)
    this.dismissZone = document.createElement('div');
    this.dismissZone.className = 'chat-notif-dismiss-zone';
    this.dismissZone.innerHTML = '<span>Release to dismiss</span>';

    // Restore position from localStorage
    this.restorePosition();

    // Attach to container
    this.container.appendChild(this.element);
    this.container.appendChild(this.dismissZone);

    // Input
    this.element.addEventListener('pointerdown', this.onPointerDown);
  }

  show(data: BubbleShowData): void {
    this.peerId = data.peerId;

    // Set profile picture
    if (data.picture) {
      this.imgEl.style.backgroundImage = `url(${data.picture})`;
      this.imgEl.textContent = '';
    } else {
      this.imgEl.style.backgroundImage = '';
      this.imgEl.textContent = (data.peerUsername ?? 'P').charAt(0).toUpperCase();
    }

    // Badge count
    if (data.unreadCount > 0) {
      this.badgeEl.textContent = data.unreadCount > 99 ? '99+' : String(data.unreadCount);
      this.badgeEl.style.display = '';
    } else {
      this.badgeEl.style.display = 'none';
    }

    this.element.classList.add('visible');
    this.visible = true;
  }

  hide(): void {
    this.element.classList.remove('visible');
    this.visible = false;
  }

  updateBadge(count: number): void {
    if (count > 0) {
      this.badgeEl.textContent = count > 99 ? '99+' : String(count);
      this.badgeEl.style.display = '';
    } else {
      this.badgeEl.style.display = 'none';
    }
  }

  isVisible(): boolean {
    return this.visible;
  }

  destroy(): void {
    this.element.removeEventListener('pointerdown', this.onPointerDown);
    document.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('pointerup', this.onPointerUp);
    if (this.holdTimer) clearTimeout(this.holdTimer);
    this.element.remove();
    this.dismissZone.remove();
    this.removeAllListeners();
  }

  // ─── Input ──────────────────────────────────────────────────────────

  private handlePointerDown(e: PointerEvent): void {
    e.preventDefault();
    e.stopPropagation();

    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;
    this.dragging = false;

    const rect = this.element.getBoundingClientRect();
    this.dragOffsetX = e.clientX - rect.left;
    this.dragOffsetY = e.clientY - rect.top;

    // Start hold timer for drag
    this.holdTimer = setTimeout(() => {
      this.dragging = true;
      this.element.classList.add('dragging');
      this.dismissZone.classList.add('visible');
    }, HOLD_DELAY);

    document.addEventListener('pointermove', this.onPointerMove);
    document.addEventListener('pointerup', this.onPointerUp);
  }

  private handlePointerMove(e: PointerEvent): void {
    const dx = e.clientX - this.dragStartX;
    const dy = e.clientY - this.dragStartY;

    // If moved enough, start drag immediately
    if (!this.dragging && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
      if (this.holdTimer) clearTimeout(this.holdTimer);
      this.dragging = true;
      this.element.classList.add('dragging');
      this.dismissZone.classList.add('visible');
    }

    if (this.dragging) {
      const x = e.clientX - this.dragOffsetX;
      const y = e.clientY - this.dragOffsetY;

      // Clamp to container
      const maxX = this.container.clientWidth - BUBBLE_SIZE;
      const maxY = this.container.clientHeight - BUBBLE_SIZE;
      const clampedX = Math.max(0, Math.min(x, maxX));
      const clampedY = Math.max(0, Math.min(y, maxY));

      this.element.style.left = `${clampedX}px`;
      this.element.style.top = `${clampedY}px`;
      this.element.style.right = 'auto';
      this.element.style.bottom = 'auto';

      // Check if over dismiss zone
      const inDismiss = clampedY > (this.container.clientHeight - DISMISS_ZONE_HEIGHT - BUBBLE_SIZE);
      this.dismissZone.classList.toggle('active', inDismiss);
    }
  }

  private handlePointerUp(e: PointerEvent): void {
    document.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('pointerup', this.onPointerUp);

    if (this.holdTimer) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }

    if (this.dragging) {
      this.element.classList.remove('dragging');
      this.dismissZone.classList.remove('visible', 'active');

      // Check dismiss zone
      const rect = this.element.getBoundingClientRect();
      const containerRect = this.container.getBoundingClientRect();
      const relativeY = rect.top - containerRect.top;
      if (relativeY > (this.container.clientHeight - DISMISS_ZONE_HEIGHT - BUBBLE_SIZE)) {
        this.hide();
        this.emit('bubble:dismiss', undefined);
        return;
      }

      // Save position
      this.savePosition();
    } else {
      // It was a tap
      const dx = Math.abs(e.clientX - this.dragStartX);
      const dy = Math.abs(e.clientY - this.dragStartY);
      if (dx < 10 && dy < 10) {
        this.emit('bubble:tap', { peerId: this.peerId });
      }
    }

    this.dragging = false;
  }

  // ─── Position persistence ───────────────────────────────────────────

  private savePosition(): void {
    try {
      const rect = this.element.getBoundingClientRect();
      const containerRect = this.container.getBoundingClientRect();
      const pos = {
        xPercent: (rect.left - containerRect.left) / containerRect.width,
        yPercent: (rect.top - containerRect.top) / containerRect.height,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch (_e) {
      // Ignore storage errors
    }
  }

  private restorePosition(): void {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const pos = JSON.parse(saved);
        if (typeof pos.xPercent === 'number' && typeof pos.yPercent === 'number') {
          const x = pos.xPercent * this.container.clientWidth;
          const y = pos.yPercent * this.container.clientHeight;
          this.element.style.left = `${x}px`;
          this.element.style.top = `${y}px`;
          this.element.style.right = 'auto';
          this.element.style.bottom = 'auto';
          return;
        }
      }
    } catch (_e) {
      // Ignore
    }

    // Default: bottom-right
    this.element.style.right = '16px';
    this.element.style.bottom = '120px';
  }

  // ─── Styles ─────────────────────────────────────────────────────────

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .chat-notif-bubble {
        position: absolute;
        width: ${BUBBLE_SIZE}px;
        height: ${BUBBLE_SIZE}px;
        z-index: 700;
        cursor: pointer;
        user-select: none;
        touch-action: none;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.25s ease, transform 0.25s ease;
        transform: scale(0.6);
      }

      .chat-notif-bubble.visible {
        opacity: 1;
        pointer-events: auto;
        transform: scale(1);
      }

      .chat-notif-bubble.dragging {
        transition: none;
        transform: scale(1.15);
      }

      .chat-notif-bubble-img {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        border: 2px solid #FF0080;
        box-sizing: border-box;
        box-shadow: 0 0 12px rgba(255, 0, 128, 0.4), 0 2px 8px rgba(0, 0, 0, 0.5);
        background-color: #242429;
        background-size: cover;
        background-position: center;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 20px;
        color: #FF0080;
        overflow: hidden;
      }

      .chat-notif-bubble.dragging .chat-notif-bubble-img {
        box-shadow: 0 0 20px rgba(255, 0, 128, 0.6), 0 4px 16px rgba(0, 0, 0, 0.6);
      }

      .chat-notif-bubble-badge {
        position: absolute;
        top: -4px;
        right: -4px;
        min-width: ${BADGE_SIZE}px;
        height: ${BADGE_SIZE}px;
        border-radius: ${BADGE_SIZE / 2}px;
        background: #FF3366;
        color: #fff;
        font-family: 'Rajdhani', sans-serif;
        font-size: 11px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 4px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
        z-index: 701;
      }

      .chat-notif-dismiss-zone {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: ${DISMISS_ZONE_HEIGHT}px;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 699;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s ease;
        border-top: 2px dashed rgba(255, 51, 102, 0.3);
        background: linear-gradient(0deg, rgba(255, 51, 102, 0.1), transparent);
      }

      .chat-notif-dismiss-zone.visible {
        opacity: 1;
      }

      .chat-notif-dismiss-zone.active {
        border-top-color: #FF3366;
        background: linear-gradient(0deg, rgba(255, 51, 102, 0.25), transparent);
      }

      .chat-notif-dismiss-zone span {
        font-family: 'Rajdhani', sans-serif;
        font-size: 12px;
        font-weight: 600;
        color: #FF3366;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        opacity: 0.8;
      }
    `;
    document.head.appendChild(style);
  }
}
